import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useExperiment } from '../../contexts/ExperimentContext';
import { useTracker } from '../../contexts/TrackerContext';
import { useMouseTracking } from '../../hooks/useMouseTracking';
// import ProgressBar from '../common/ProgressBar';
import MicroSurvey from '../common/MicroSurvey';
import EmailDecisionBar from '../common/EmailDecisionBar';
import './EmailTask.css';
import './EmailTask.ftt.css';
import { calibrator } from '../../utils/adaptiveCalibrator';
import { emailScenarios, getScenariosByCongruence, balanceScenarios, EmailScenario as ImportedEmailScenario } from '../../data/emailScenariosWithCongruence';
import { getOrgConfig } from '../../data/orgConfigs';
import { resolveOrgTemplates } from '../../utils/resolveOrgTemplates';
import { 
  SeededRandom, 
  generateOrderSeed, 
  createPresentationOrderRecord, 
  validateEmailOrder,
  EmailPresentationOrder 
} from '../../utils/seededRandom';
import { savePhaseData } from '../../services/api';

// TypeScript interfaces for email data structures
export interface EmailData extends ImportedEmailScenario {
  decision?: 'engage' | 'verify' | 'ignore' | 'report' | null;
  needsDecision?: boolean;
  // Adaptive calibration properties
  timeLimit?: number;
  timePressure?: boolean;
  // Tracking order
  presentationOrder?: number;
}

interface MicroSurveyContext {
  emailId: string;
  isPhishing: boolean;
  linkUrl?: string;
  linkText?: string;
  action?: string;
  presentationOrder?: number;
}

interface LinkClickData {
  emailId: string;
  isPhishing: boolean;
  url: string;
  text?: string;
  threatIndicators: string[];
  timeToClick: number;
  processingTime: number;
  processingMode: 'automatic' | 'deliberate';
}

// Email scenarios generator with congruence support
function generateEmailScenarios(condition: any, participantId?: string): {
  scenarios: EmailData[];
  presentationOrder: EmailPresentationOrder;
} {
  // Get balanced scenarios with Latin Square counterbalancing (2×2 design: Congruence × Truth)
  // Algorithm guarantees: 1 Congruent-Safe, 1 Congruent-Risky, 1 Incongruent-Safe, 1 Incongruent-Risky (4 total)
  // Domains rotate across participants for cross-participant balance
  let selectedScenarios = getScenariosByCongruence(participantId);

  // Ensure balanced phishing/legitimate distribution
  selectedScenarios = balanceScenarios(selectedScenarios);

  // Apply org-specific branding (domain names, department names, etc.)
  const orgConfig = getOrgConfig();
  selectedScenarios = selectedScenarios.map(s => resolveOrgTemplates(s, orgConfig));
  
  // Store original order before shuffling
  const originalScenarios = [...selectedScenarios];
  
  // Convert to EmailData format with additional properties
  const scenarios: EmailData[] = selectedScenarios.map(scenario => ({
    ...scenario,
    decision: null,
    needsDecision: true
  }));
  
  // Generate deterministic seed for reproducible randomization
  const orderSeed = generateOrderSeed(participantId || 'unknown', {
    taskType: 'email',
    timestamp: Date.now(),
    congruence: 'balanced-2x2x2x2' // All participants receive balanced distribution
  });

  // Use seeded randomization for auditability
  const rng = new SeededRandom(orderSeed);
  const shuffledScenarios = rng.shuffle(scenarios);

  // Create complete presentation order record
  const presentationOrder = createPresentationOrderRecord(
    participantId || 'unknown',
    'email',
    'balanced-2x2x2x2', // All participants receive balanced distribution
    originalScenarios,
    shuffledScenarios,
    orderSeed
  );
  
  // Validate the order
  const validation = validateEmailOrder(presentationOrder);
  if (!validation.isValid) {
    console.error('Email order validation failed:', validation.issues);
  }
  if (validation.warnings.length > 0) {
    console.warn('Email order warnings:', validation.warnings);
  }
  
  console.log('[EmailTask] Generated presentation order:', {
    participantId,
    seed: orderSeed,
    emailOrder: presentationOrder.emailOrder.map(e => ({
      id: e.emailId,
      pos: e.presentationIndex,
      orig: e.originalIndex,
      type: e.isPhishing ? 'P' : 'L',
      cong: e.congruence[0].toUpperCase()
    })),
    validation: validation.isValid ? 'PASS' : 'FAIL'
  });
  
  return { scenarios: shuffledScenarios, presentationOrder };
}

function EmailTask() {
  const navigate = useNavigate();
  const { experimentData, updateProgress, updateFTTClassification } = useExperiment();
  const { 
    tracker, 
    trackMouseMovement, 
    trackScroll, 
    trackHover, 
    trackEmailInteraction,
    classifyCurrentResponse 
  } = useTracker();
  
  const [emails, setEmails] = useState<EmailData[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<EmailData | null>(null);
  const [showMicroSurvey, setShowMicroSurvey] = useState(false);
  const [microSurveyContext, setMicroSurveyContext] = useState<MicroSurveyContext | null>(null);
  const [decisionsLoaded, setDecisionsLoaded] = useState(false);
  const [isEmailListExpanded, setIsEmailListExpanded] = useState<boolean>(false);
  const [isResizing, setIsResizing] = useState(false);
  const [emailListWidth, setEmailListWidth] = useState(350);
  
  // Track email responses for FTT analysis
  const emailResponses = useRef(new Map<string, any>());
  const [lastActionTime, setLastActionTime] = useState(Date.now());
  const emailProcessingSequence = useRef<string[]>([]);
  
  // Calculate cognitive load for FTT context
  const calculateCognitiveLoad = () => {
    const timeInTask = Date.now() - phaseStartTime.current;
    const responsesCount = emailResponses.current.size;
    const recentHesitations = tracker.events.filter(
      e => e.eventType === 'hesitation' && e.timestamp > Date.now() - 30000
    ).length;
    
    // Simple cognitive load calculation (0-1 scale)
    const timeFactor = Math.min(timeInTask / (10 * 60 * 1000), 1); // 10 minutes max
    const responseFactor = Math.min(responsesCount / 20, 1); // 20 responses max
    const hesitationFactor = Math.min(recentHesitations / 10, 1); // 10 hesitations max
    
    return (timeFactor + responseFactor + hesitationFactor) / 3;
  };
  
  const phaseStartTime = useRef(Date.now());
  const emailContainerRef = useRef<HTMLDivElement>(null);
  const emailListRef = useRef<HTMLDivElement>(null);
  const { reset: resetMouseTracking, getMeasures: getMouseMeasures } = useMouseTracking(emailContainerRef);
  
  // Calculate time limit based on condition
  const timeRemaining = experimentData?.participant?.conditions?.timePressure === 'high' ? 300 : 600; // 5 minutes for high pressure, 10 minutes for low

  // Calibration update effect
  useEffect(() => {
    // Update calibrator with recent performance
    const responses = Array.from(emailResponses.current.values());
    if (responses.length > 0 && experimentData?.participant?.id) {
      calibrator.analyzePerformance(experimentData.participant.id, responses);
    }
  }, [emailResponses.current.size]);
  
  // Helper function to get decision storage key
  const getDecisionStorageKey = () => {
    return `emailDecisions_${experimentData?.participant?.id}`;
  };

  // Save decisions to localStorage whenever they change
  const saveDecisionsToStorage = (updatedEmails: EmailData[]) => {
    if (!experimentData?.participant?.id) return;
    
    const decisions: Record<string, string> = {};
    updatedEmails.forEach(email => {
      if (email.decision) {
        decisions[email.id] = email.decision;
      }
    });
    
    localStorage.setItem(getDecisionStorageKey(), JSON.stringify(decisions));
  };

  // Load decisions from localStorage
  const loadDecisionsFromStorage = (): Record<string, string> => {
    if (!experimentData?.participant?.id) return {};
    
    try {
      const stored = localStorage.getItem(getDecisionStorageKey());
      return stored ? JSON.parse(stored) : {};
    } catch (e) {
      console.warn('Failed to parse email decisions from localStorage:', e);
      return {};
    }
  };

  useEffect(() => {
    // Only initialize emails once when component mounts and experimentData is available
    if (!experimentData?.participant?.id || emails.length > 0) return;
    
    // Initialize email scenarios with seeded randomization and presentation order tracking
    const { scenarios, presentationOrder } = generateEmailScenarios(experimentData.participant.conditions || {}, experimentData.participant.id);
    
    // Apply adaptive calibration and add presentation order from the seeded randomization  
    const calibratedScenarios = scenarios.map((scenario: EmailData, index: number) => {
      const adjusted = calibrator.getAdjustedScenario(scenario, experimentData.participant.id);
      // Mark emails that need decisions (initially unread emails)
      return {
        ...adjusted,
        needsDecision: adjusted.unread,
        presentationOrder: index + 1 // Use the seeded order from generateEmailScenarios
      };
    });
    
    // Load saved decisions from localStorage
    const savedDecisions = loadDecisionsFromStorage();
    const scenariosWithDecisions = calibratedScenarios.map(email => {
      if (savedDecisions[email.id]) {
        return {
          ...email,
          decision: savedDecisions[email.id] as 'engage' | 'verify' | 'ignore' | 'report'
        };
      }
      return email;
    });
    
    setEmails(scenariosWithDecisions);
    setDecisionsLoaded(true);
    
    // Store presentation order in database for complete auditability
    const storePresentationOrder = async () => {
      try {
        await savePhaseData(experimentData.participant.id, 'email-presentation-order', presentationOrder);
        console.log('[EmailTask] Presentation order stored in database successfully');
      } catch (error) {
        console.error('[EmailTask] Failed to store presentation order:', error);
      }
    };
    storePresentationOrder();
    
    // Track phase start with comprehensive presentation order data
    const unreadCount = calibratedScenarios.filter(e => e.needsDecision).length;
    
    tracker.track('phaseStart', {
      phase: 'email-task',
      emailCount: scenarios.length,
      phishingCount: scenarios.filter(e => e.isPhishing).length,
      condition: experimentData.participant.conditions || {},
      congruenceCondition: 'balanced-2x2x2x2', // All participants receive balanced distribution
      initialUnreadCount: unreadCount,
      // Include the complete presentation order record for full auditability
      presentationOrderRecord: presentationOrder,
      emailDetails: calibratedScenarios.map(e => ({
        id: e.id,
        isPhishing: e.isPhishing,
        congruence: e.congruence,
        conflictLevel: e.conflictLevel,
        sender: e.sender,
        subject: e.subject,
        presentationOrder: e.presentationOrder
      }))
    });
    
    phaseStartTime.current = Date.now();
    
    // Set up periodic cognitive load checks
    const intervalId = setInterval(() => {
      tracker.track('cognitiveLoadCheck', {
        phase: 'email-task',
        timeElapsed: Date.now() - phaseStartTime.current
      });
    }, 30000); // Every 30 seconds
    
    return () => clearInterval(intervalId);
  }, [experimentData?.participant?.id, tracker]);

  // Fetch existing decisions from backend on mount (recovery mechanism)
  useEffect(() => {
    if (!decisionsLoaded || !experimentData?.participant?.id) return;
    
    // Query backend for existing email responses
    const fetchExistingDecisions = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/experiments/${experimentData.participant.id}`);
        if (response.ok) {
          const data = await response.json();
          const backendResponses = data.participant?.emailTaskData?.responses || [];
          
          // If backend has more decisions than localStorage, update our state
          if (backendResponses.length > 0) {
            const backendDecisions: Record<string, string> = {};
            backendResponses.forEach((r: any) => {
              if (r.action && r.emailId) {
                backendDecisions[r.emailId] = r.action;
              }
            });
            
            // Merge with current state
            const updatedEmails = emails.map(email => {
              if (backendDecisions[email.id] && !email.decision) {
                return { ...email, decision: backendDecisions[email.id] as any };
              }
              return email;
            });
            
            setEmails(updatedEmails);
            saveDecisionsToStorage(updatedEmails);
            console.log('Recovered decisions from backend:', Object.keys(backendDecisions).length);
          }
        }
      } catch (error) {
        console.error('Failed to fetch existing decisions:', error);
      }
    };
    
    fetchExistingDecisions();
  }, [decisionsLoaded, experimentData?.participant?.id]);
  
  // Track email detail view changes
  useEffect(() => {
    if (selectedEmail) {
      // Track email open event
      trackEmailInteraction(selectedEmail.id, 'open');
      
      return () => {
        // Track email close event when switching to another email
        trackEmailInteraction(selectedEmail.id, 'close');
      };
    }
  }, [selectedEmail?.id, trackEmailInteraction]);


  const handleLinkClick = (email: EmailData, link: any, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    // Get mouse tracking measures
    const mouseMeasures = getMouseMeasures();
    
    // Calculate response time and context for FTT
    const responseTime = Date.now() - lastActionTime;
    const responseId = `email-${email.id}-${Date.now()}`;
    
    // Build FTT context
    const fttContext = {
      isPhishing: email.isPhishing,
      scenarioComplexity: email.complexity || 0.5,
      priorResponses: emailResponses.current.size,
      cognitiveLoad: calculateCognitiveLoad()
    };
    
    // Get FTT classification
    const fttClassification = classifyCurrentResponse(responseId, responseTime, fttContext);
    
    // Store in experiment context for cross-domain analysis
    updateFTTClassification('email', responseId, fttClassification);

    const linkData: LinkClickData = {
      emailId: email.id,
      isPhishing: email.isPhishing,
      url: link.url,
      text: link.text,
      threatIndicators: email.threatIndicators || [],
      timeToClick: Date.now() - phaseStartTime.current,
      processingTime: responseTime,
      processingMode: fttClassification.mode === 'gist' ? 'automatic' : 'deliberate'
    };
    
    // Store response with FTT data
    emailResponses.current.set(email.id, {
      ...linkData,
      fttClassification,
      clicked: true,
      timestamp: Date.now()
    });

    // Track with mouse behavior data and FTT classification
    tracker.track('linkClick', {
      ...linkData,
      presentationOrder: email.presentationOrder,
      taskHalf: (email.presentationOrder || 1) <= 2 ? 'first' : 'second',
      congruence: email.congruence,
      gistCues: email.gistCues,
      verbatimCues: email.verbatimCues,
      conflictLevel: email.conflictLevel,
      mouseTracking: mouseMeasures,
      fttClassification
    });

    // Silently record the click without any feedback
    
    // Update last action time for processing mode calculation
    setLastActionTime(Date.now());
  };

  const handleMicroSurveyResponse = (response: {
    reason: string;
    customReason?: string;
    reasonCategory?: 'gist' | 'verbatim' | 'other' | 'task_need' | 'external_pressure' | 'motivation' | 'risk_assessment';
    confidence?: number;
    metacognitiveCertainty?: number;
    perceivedTimePressure?: number;
    rankedReasons?: Array<{
      rank: number;
      value: string;
      label: string;
      category: 'gist' | 'verbatim';
    }>;
    gistDominanceScore?: number;
    presentedOrder?: string[];
    selectedIndices?: number[];
    primaryReasonKeySelected?: string;
    routeSelected?: 'gist' | 'verbatim';
    gistOrder?: string[];
    verbatimOrder?: string[];
    selectedIndexWithinColumn?: number;
    decisionLatencyMs?: number;
    totalMicroSurveyTimeMs?: number;
    step1TimeMs?: number | null;
    step2TimeMs?: number | null;
  }) => {
    console.log('=== handleMicroSurveyResponse called ===');
    console.log('microSurveyContext:', microSurveyContext);
    console.log('response:', response);
    console.log('confidence:', response.confidence);

    if (microSurveyContext) {
      const { emailId, action } = microSurveyContext;
      console.log('Processing email decision for:', emailId, 'with action:', action);

      // Compute FTT trajectory classification for this email decision
      // (mirrors the DeviceTask pattern — same classifier, same context shape)
      const emailScenario = emails.find(e => e.id === emailId);
      const decisionResponseTime = Date.now() - lastActionTime;
      const fttResponseId = `email-${emailId}-${Date.now()}`;
      const fttContext = {
        // Do NOT feed ground-truth veracity or stimulus complexity into the processing-mode
        // classifier — both are independent variables; letting them set the DV is circular.
        // (Matches the InformationSharingTask decontamination.) Mode stays exploratory;
        // confirmatory FTT uses the conjoint-recognition memory traces.
        isPhishing: false,
        scenarioComplexity: 0.5,
        priorResponses: emailResponses.current.size,
        cognitiveLoad: calculateCognitiveLoad()
      };
      const fttClassification = classifyCurrentResponse(fttResponseId, decisionResponseTime, fttContext);
      updateFTTClassification('email', fttResponseId, fttClassification);
      console.log('FTT classification for email', emailId, ':', fttClassification?.mode);

      // Now finalize the decision after survey submission
      const updatedEmails = emails.map(email =>
        email.id === emailId ? { ...email, decision: action as any } : email
      );
      setEmails(updatedEmails);

      // Save to localStorage for persistence
      saveDecisionsToStorage(updatedEmails);

      // Track the decision with sequence
      const sequencePosition = emailProcessingSequence.current.length + 1;
      if (!emailProcessingSequence.current.includes(emailId)) {
        emailProcessingSequence.current.push(emailId);
      }

      // Track the email decision (now includes FTT classification)
      console.log('Tracking emailDecision event...');
      tracker.track('emailDecision', {
        emailId,
        decision: action,
        isPhishing: microSurveyContext.isPhishing,
        domain: emailScenario?.domain,
        emailCategory: emailScenario?.emailCategory,
        congruence: emailScenario?.congruence,
        gistCues: emailScenario?.gistCues,
        verbatimCues: emailScenario?.verbatimCues,
        conflictLevel: emailScenario?.conflictLevel,
        fttClassification,
        timestamp: Date.now(),
        responseTime: decisionResponseTime,
        processingMode: fttClassification.mode === 'gist' ? 'automatic' : 'deliberate',
        sequencePosition,
        presentationOrder: microSurveyContext.presentationOrder,
        taskHalf: (microSurveyContext.presentationOrder || 1) <= 2 ? 'first' : 'second',
        processingOrder: [...emailProcessingSequence.current]
      });
      console.log('emailDecision event tracked');

      // Track the survey response with FTT categorization and ALL microsurvey data
      tracker.track('microSurveyResponse', {
        ...microSurveyContext,
        taskType: 'email',
        response: response.reason,
        customReason: response.customReason,
        reasonCategory: response.reasonCategory || 'other',
        confidence: response.confidence,
        metacognitiveCertainty: response.metacognitiveCertainty,
        perceivedTimePressure: response.perceivedTimePressure,
        rankedReasons: response.rankedReasons,
        gistDominanceScore: response.gistDominanceScore,
        presentedOrder: response.presentedOrder,
        selectedIndices: response.selectedIndices,
        primaryReasonKeySelected: response.primaryReasonKeySelected,
        routeSelected: response.routeSelected,
        gistOrder: response.gistOrder,
        verbatimOrder: response.verbatimOrder,
        selectedIndexWithinColumn: response.selectedIndexWithinColumn,
        decisionLatencyMs: response.decisionLatencyMs,
        totalMicroSurveyTimeMs: response.totalMicroSurveyTimeMs,
        step1TimeMs: response.step1TimeMs,
        step2TimeMs: response.step2TimeMs,
        neutralizationType: classifyNeutralization(response.reason),
        fttAlignment: response.reasonCategory, // How their stated reason aligns with FTT classification
        phase: 'email-task'
      });
      console.log('microSurveyResponse tracked with confidence:', response.confidence);

      // Maintain a complete phase-level response for every decision, including
      // decisions made without clicking a link. This is the independent backup
      // to the per-event tracking path.
      if (emailId) {
        const existingResponse = emailResponses.current.get(emailId) || {};
        emailResponses.current.set(emailId, {
          ...existingResponse,
          emailId,
          action,
          isPhishing: microSurveyContext.isPhishing,
          domain: emailScenario?.domain,
          emailCategory: emailScenario?.emailCategory,
          congruence: emailScenario?.congruence,
          gistCues: emailScenario?.gistCues,
          verbatimCues: emailScenario?.verbatimCues,
          conflictLevel: emailScenario?.conflictLevel,
          responseTime: decisionResponseTime,
          processingMode: fttClassification.mode === 'gist' ? 'automatic' : 'deliberate',
          presentationOrder: microSurveyContext.presentationOrder,
          taskHalf: (microSurveyContext.presentationOrder || 1) <= 2 ? 'first' : 'second',
          fttClassification,
          microSurveyResponse: response,
          taskState: 'complete',
          timestamp: existingResponse.timestamp || Date.now()
        });
      }
    }
    setShowMicroSurvey(false);
    setMicroSurveyContext(null);
  };

  const classifyNeutralization = (response: string): string => {
    const neutralizationMap: { [key: string]: string } = {
      // Gist-based reasons (quick impressions)
      'looked_legitimate': 'general_justification',
      'needed_task': 'defense_of_necessity',
      'time_pressure': 'defense_of_necessity',
      'minimal_risk': 'denial_of_injury',
      'no_harm': 'denial_of_injury',
      'curious': 'other_motivation',
      
      // Verbatim-based reasons (detailed checks)
      'checked_sender': 'rational_evaluation',
      'examined_url': 'rational_evaluation',
      'noticed_grammar': 'rational_evaluation',
      'verified_context': 'rational_evaluation',
      'checked_domain': 'rational_evaluation',
      'analyzed_content': 'rational_evaluation',
      
      // Other
      'other': 'other'
    };
    return neutralizationMap[response] || 'unknown';
  };

  // Handle email decision making
  const handleEmailDecision = (emailId: string, decision: 'engage' | 'verify' | 'ignore' | 'report') => {
    // Check if email already has a decision (prevent re-clicking)
    const existingEmail = emails.find(e => e.id === emailId);
    if (existingEmail?.decision) {
      return; // Already processed, ignore new clicks
    }

    // Don't update email state yet - wait for micro-survey submission
    // Just trigger the micro-survey with the pending decision
    const email = emails.find(e => e.id === emailId);
    if (email) {
      setMicroSurveyContext({
        emailId: email.id,
        isPhishing: email.isPhishing,
        action: decision,
        presentationOrder: email.presentationOrder
      });
      setShowMicroSurvey(true);
    }

    setLastActionTime(Date.now());
  };

  // Check if all emails that need decisions have been processed
  const getAllEmailsProcessed = () => {
    const emailsNeedingDecisions = emails.filter(email => email.needsDecision);
    return emailsNeedingDecisions.every(email => email.decision);
  };

  // Get emails that still need decisions
  const getUnprocessedEmails = () => {
    return emails.filter(email => email.needsDecision && !email.decision);
  };

  // Get progress statistics
  const getProgressStats = () => {
    const emailsNeedingDecisions = emails.filter(email => email.needsDecision);
    const emailsWithDecisions = emailsNeedingDecisions.filter(email => email.decision);
    
    return {
      totalUnreadEmails: emailsNeedingDecisions.length,
      decidedUnreadEmails: emailsWithDecisions.length,
      processedEmails: emailsWithDecisions.length,
      processedPercentage: emailsNeedingDecisions.length > 0 ? Math.round((emailsWithDecisions.length / emailsNeedingDecisions.length) * 100) : 100
    };
  };

  const handleComplete = async () => {
    // Validate that all emails have been processed
    if (!getAllEmailsProcessed()) {
      const unprocessedCount = getUnprocessedEmails().length;
      alert(`Please make decisions on all emails before completing. You have ${unprocessedCount} emails remaining.`);
      return;
    }

    // Count link clicks and decisions from tracker events
    const linkClickCount = tracker.events.filter(e => e.eventType === 'linkClick').length;
    const stats = getProgressStats();
    const viewedEmails = emails.filter(email => !email.unread).length;
    const summary = { 
      emailsClicked: linkClickCount,
      emailsViewed: viewedEmails,
      emailsDecided: stats.decidedUnreadEmails,
      emailsProcessed: stats.processedEmails
    };
    
    // Track engagement decisions for analysis
    const engagementSummary = {
      engage: emails.filter(e => e.decision === 'engage').length,
      verify: emails.filter(e => e.decision === 'verify').length,
      ignore: emails.filter(e => e.decision === 'ignore').length,
      report: emails.filter(e => e.decision === 'report').length,
      totalPhishingDetected: emails.filter(e =>
        e.isPhishing && (e.decision === 'report' || e.decision === 'verify' || e.decision === 'ignore')
      ).length,
      totalPhishingEngaged: emails.filter(e => e.isPhishing && e.decision === 'engage').length
    };
    
    tracker.track('phaseComplete', {
      phase: 'email-task',
      duration: Date.now() - phaseStartTime.current,
      emailsClicked: emails.filter(e => !e.unread).length,
      phishingLinksClicked: tracker.events.filter(e =>
        e.eventType === 'linkClick' && e.isPhishing
      ).length,
      summary,
      engagementSummary,
      allEmailsProcessed: true,
      completeProcessingSequence: emailProcessingSequence.current,
      viewingOrder: emailProcessingSequence.current.filter(id => id.startsWith('view-')).map(id => id.replace('view-', '')),
      decisionOrder: emailProcessingSequence.current.filter(id => !id.startsWith('view-'))
    });

    // Save email task data to database (critical - tracker events alone are not sufficient)
    const participantId = experimentData?.participant?.id || localStorage.getItem('participantId');
    if (participantId) {
      const emailPhaseData = {
        ...summary,
        engagementSummary,
        duration: Date.now() - phaseStartTime.current,
        responses: Array.from(emailResponses.current.values()),
        emailDetails: emails.map(e => ({
          id: e.id,
          isPhishing: e.isPhishing,
          congruence: e.congruence,
          conflictLevel: e.conflictLevel,
          decision: e.decision,
          presentationOrder: e.presentationOrder,
          domain: e.domain
        })),
        completedAt: new Date().toISOString()
      };
      // Await so the save (or its localStorage retry-queue write) completes
      // BEFORE we clear the decision backup and navigate away/unmount.
      try {
        await savePhaseData(participantId, 'email-task', emailPhaseData);
        console.log('Email task data saved to database');
        // Only clear the local decision backup once the save resolved/queued.
        localStorage.removeItem(getDecisionStorageKey());
      } catch (error: any) {
        console.error('Failed to save email task data (queued for retry, backup kept):', error);
      }
    } else {
      localStorage.removeItem(getDecisionStorageKey());
    }
    
    // Navigate based on two-cohort system
    const taskAssignment = experimentData?.participant?.taskAssignment;
    const cohort = taskAssignment?.cohort;
    const taskOrder = taskAssignment?.taskOrder;
    
    console.log('EmailTask completion navigation:', {
      cohort: cohort,
      taskOrder: taskOrder,
      taskAssignment: taskAssignment
    });
    
    if (cohort === 'email-device') {
      if (taskOrder === 'email-first') {
        updateProgress(60); // 60% complete after first task
        navigate('/filler-task'); // Go to filler task before device task
      } else {
        updateProgress(70); // 70% complete after second task
        navigate('/post-experiment'); // Complete - go to post-experiment
      }
    } else if (cohort === 'email-coding') {
      if (taskOrder === 'email-first') {
        updateProgress(60); // 60% complete after first task
        navigate('/filler-task'); // Go to filler task before coding task
      } else {
        updateProgress(70); // 70% complete after second task
        navigate('/post-experiment'); // Complete - go to post-experiment
      }
    } else {
      // Fallback for any edge cases
      console.warn('EmailTask completed but cohort assignment unclear');
      navigate('/post-experiment');
    }
  };

  const handleTimeout = () => {
    tracker.track('taskTimeout', {
      phase: 'email-task',
      completed: false,
      emailsProcessed: emails.filter(e => !e.unread).length
    });
    handleComplete();
  };

  // Toggle email list width like real Outlook
  const toggleEmailListWidth = () => {
    setIsEmailListExpanded(!isEmailListExpanded);
  };

  type EmailActionType = 'delete' | 'archive' | 'reportSpam' | 'reply' | 'replyAll' | 'forward';
  
  const handleEmailAction = (action: EmailActionType, email: EmailData) => {
    console.log('handleEmailAction called:', action, email?.id);
    
    // Track the action
    tracker.track('emailAction', {
      action,
      emailId: email.id,
      isPhishing: email.isPhishing,
      sender: email.sender,
      subject: email.subject,
      responseTime: Date.now() - lastActionTime,
      timestamp: Date.now()
    });

    // Update UI based on action
    switch (action) {
      case 'delete':
        // Remove email from list
        setEmails(emails.filter(e => e.id !== email.id));
        setSelectedEmail(null);
        break;
      case 'archive':
        // Remove email from list (simulate archiving)
        setEmails(emails.filter(e => e.id !== email.id));
        setSelectedEmail(null);
        break;
      case 'reportSpam':
        // Mark as spam and remove
        setEmails(emails.filter(e => e.id !== email.id));
        setSelectedEmail(null);
        // Track special spam report event
        tracker.track('spamReport', {
          emailId: email.id,
          isPhishing: email.isPhishing,
          correctIdentification: email.isPhishing
        });
        break;
      case 'reply':
      case 'replyAll':
      case 'forward':
        // Track engagement but don't remove email
        // Silently record without feedback
        break;
    }

    // Track email interaction sequence
    trackEmailInteraction(email.id, action as any);
    setLastActionTime(Date.now());
  };

  // Removed action feedback - we silently record actions without feedback

  return (
    <div className="email-task">

      <div className="email-client">
        <div className="email-header">
          <h2>Outlook Mail</h2>
          <div className="email-progress">
            <span className="progress-text">
              Decisions made: {getProgressStats().decidedUnreadEmails}/{getProgressStats().totalUnreadEmails}
            </span>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${getProgressStats().processedPercentage}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Outlook-style toolbar — purely decorative. All actions go through the 3 decision buttons at the bottom. */}
        <div className="email-toolbar">
          <button className="toolbar-button primary" onClick={(e) => { e.preventDefault(); alert('This option is disabled. Please use the 3 decision buttons at the bottom of the email.'); }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 2C8.27614 2 8.5 2.22386 8.5 2.5V7.5H13.5C13.7761 7.5 14 7.72386 14 8C14 8.27614 13.7761 8.5 13.5 8.5H8.5V13.5C8.5 13.7761 8.27614 14 8 14C7.72386 14 7.5 13.7761 7.5 13.5V8.5H2.5C2.22386 8.5 2 8.27614 2 8C2 7.72386 2.22386 7.5 2.5 7.5H7.5V2.5C7.5 2.22386 7.72386 2 8 2Z"/>
            </svg>
            New mail
          </button>
          <button className="toolbar-button" onClick={(e) => { e.preventDefault(); alert('This option is disabled. Please use the 3 decision buttons at the bottom of the email.'); }}>Reply</button>
          <button className="toolbar-button" onClick={(e) => { e.preventDefault(); alert('This option is disabled. Please use the 3 decision buttons at the bottom of the email.'); }}>Reply all</button>
          <button className="toolbar-button" onClick={(e) => { e.preventDefault(); alert('This option is disabled. Please use the 3 decision buttons at the bottom of the email.'); }}>Forward</button>
          <div className="toolbar-separator"></div>
          <button className="toolbar-button" onClick={(e) => { e.preventDefault(); alert('This option is disabled. Please use the 3 decision buttons at the bottom of the email.'); }}>🗑️ Delete</button>
          <button className="toolbar-button" onClick={(e) => { e.preventDefault(); alert('This option is disabled. Please use the 3 decision buttons at the bottom of the email.'); }}>⊘ Report Spam</button>
        </div>

        <div 
          className="email-container" 
          ref={emailContainerRef}
          onMouseMove={(e) => trackMouseMovement(e)}
        >
          {/* Folder pane */}
          <div className="folder-pane">
            <div className="folder-header">Folders</div>
            <div className="folder-item active">
              <svg className="folder-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M14 5.5V13.5C14 14.33 13.33 15 12.5 15H3.5C2.67 15 2 14.33 2 13.5V5.5L8 10L14 5.5ZM12.5 4C13.33 4 14 4.67 14 5.5L8 10L2 5.5C2 4.67 2.67 4 3.5 4H12.5Z"/>
              </svg>
              <span>Inbox</span>
              <span className="folder-count">{emails.filter(e => e.unread).length}</span>
            </div>
            <div className="folder-item">
              <svg className="folder-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M13 4H10L9 3H3C2.45 3 2 3.45 2 4V12C2 12.55 2.45 13 3 13H13C13.55 13 14 12.55 14 12V5C14 4.45 13.55 4 13 4Z"/>
              </svg>
              <span>Drafts</span>
            </div>
            <div className="folder-item">
              <svg className="folder-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M2 4L8 9L14 4V5.5L8 10.5L2 5.5V4M2 4C2 3.45 2.45 3 3 3H13C13.55 3 14 3.45 14 4L8 9L2 4Z"/>
              </svg>
              <span>Sent Items</span>
            </div>
            <div className="folder-item">
              <svg className="folder-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M12 3.5H10.5V3C10.5 2.45 10.05 2 9.5 2H6.5C5.95 2 5.5 2.45 5.5 3V3.5H4C3.45 3.5 3 3.95 3 4.5V5H13V4.5C13 3.95 12.55 3.5 12 3.5ZM12 6H4L4.5 13.5C4.5 14.05 4.95 14.5 5.5 14.5H10.5C11.05 14.5 11.5 14.05 11.5 13.5L12 6Z"/>
              </svg>
              <span>Deleted Items</span>
            </div>
            <div className="folder-item">
              <svg className="folder-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M13 4H10L9 3H3C2.45 3 2 3.45 2 4V12C2 12.55 2.45 13 3 13H13C13.55 13 14 12.55 14 12V5C14 4.45 13.55 4 13 4Z"/>
              </svg>
              <span>Archive</span>
            </div>
            <div className="folder-item">
              <svg className="folder-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 2C8.55 2 9 2.45 9 3C9 3.55 8.55 4 8 4C7.45 4 7 3.55 7 3C7 2.45 7.45 2 8 2ZM8 6C8.55 6 9 6.45 9 7C9 7.55 8.55 8 8 8C7.45 8 7 7.55 7 7C7 6.45 7.45 6 8 6ZM8 10C8.55 10 9 10.45 9 11C9 11.55 8.55 12 8 12C7.45 12 7 11.55 7 11C7 10.45 7.45 10 8 10Z"/>
              </svg>
              <span>More</span>
            </div>
          </div>

          {/* Message list */}
          <div className={`email-list ${isEmailListExpanded ? 'expanded' : 'normal'}`}>
            <div className="email-list-header">
              <div className="list-controls">
                <div className="filter-dropdown">All</div>
                <div className="list-actions">
                  <button className="icon-button" title="Filter">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M14 3H2L7 9V13L9 14V9L14 3Z"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div 
              className="email-items-container"
              ref={emailListRef}
              onScroll={trackScroll}
            >
              {emails.map((email) => (
              <div
                key={email.id}
                className={`email-item ${selectedEmail?.id === email.id ? 'selected' : ''} ${email.unread ? 'unread' : ''} ${email.decision ? 'decided' : ''}`}
                data-email-id={email.id}
                onMouseEnter={() => {
                  trackHover(`email-item-${email.id}`, 'enter', {
                    emailId: email.id,
                    isPhishing: email.isPhishing,
                    sender: email.sender,
                    subject: email.subject
                  });
                }}
                onMouseLeave={() => {
                  trackHover(`email-item-${email.id}`, 'leave', {
                    emailId: email.id,
                    isPhishing: email.isPhishing
                  });
                }}
                onClick={() => {
                  setSelectedEmail(email);
                  setLastActionTime(Date.now());

                  // Scroll to top when email opens
                  window.scrollTo({ top: 0, behavior: 'smooth' });

                  // Track viewing sequence
                  const viewSequence = emailProcessingSequence.current.filter(id => id.startsWith('view-')).length + 1;
                  if (!emailProcessingSequence.current.includes(`view-${email.id}`)) {
                    emailProcessingSequence.current.push(`view-${email.id}`);
                  }

                  trackEmailInteraction(email.id, 'view');
                  tracker.track('emailViewed', {
                    emailId: email.id,
                    isPhishing: email.isPhishing,
                    viewSequence,
                    timestamp: Date.now()
                  });
                  
                  setEmails(emails.map(e => 
                    e.id === email.id ? { ...e, unread: false } : e
                  ));
                  // Reset mouse tracking for new email
                  resetMouseTracking();
                }}
              >
                <div className="email-item-content">
                  <div className="email-avatar">
                    {email.sender.charAt(0).toUpperCase()}
                  </div>
                  <div className="email-info">
                    <div 
                      className="email-sender"
                      onMouseEnter={() => trackHover(`sender-${email.id}`, 'enter', {
                        emailId: email.id,
                        sender: email.sender,
                        isPhishing: email.isPhishing
                      })}
                      onMouseLeave={() => trackHover(`sender-${email.id}`, 'leave', {
                        emailId: email.id
                      })}
                    >{email.sender}</div>
                    <div 
                      className="email-subject"
                      onMouseEnter={() => trackHover(`subject-${email.id}`, 'enter', {
                        emailId: email.id,
                        subject: email.subject,
                        isPhishing: email.isPhishing
                      })}
                      onMouseLeave={() => trackHover(`subject-${email.id}`, 'leave', {
                        emailId: email.id
                      })}
                    >{email.subject}</div>
                    <div className="email-preview">{email.preview}</div>
                  </div>
                  <div className="email-time">{email.time}</div>
                </div>
              </div>
            ))}
            </div>
            {/* Expand/Collapse button like real Outlook */}
            <button 
              className="email-list-toggle"
              onClick={toggleEmailListWidth}
              title={isEmailListExpanded ? 'Expand email list' : 'Collapse email list to expand reading pane'}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                <path d={isEmailListExpanded ? "M8 3L4 7h8L8 3z" : "M4 3L8 7H0L4 3z"} />
              </svg>
            </button>
          </div>

          {/* Reading pane */}
          {selectedEmail ? (
            <div className="email-detail">
              <div className="email-detail-header">
                {/* Detail toolbar — decorative only. All actions go through the 3 decision buttons below. */}
                <div className="detail-toolbar">
                  <button className="toolbar-button" onClick={(e) => { e.preventDefault(); alert('This option is disabled. Please use the 3 decision buttons at the bottom of the email.'); }}>Reply</button>
                  <button className="toolbar-button" onClick={(e) => { e.preventDefault(); alert('This option is disabled. Please use the 3 decision buttons at the bottom of the email.'); }}>Reply all</button>
                  <button className="toolbar-button" onClick={(e) => { e.preventDefault(); alert('This option is disabled. Please use the 3 decision buttons at the bottom of the email.'); }}>Forward</button>
                  <div className="toolbar-separator"></div>
                  <button className="toolbar-button" onClick={(e) => { e.preventDefault(); alert('This option is disabled. Please use the 3 decision buttons at the bottom of the email.'); }}>🗑️ Delete</button>
                  <button className="toolbar-button" onClick={(e) => { e.preventDefault(); alert('This option is disabled. Please use the 3 decision buttons at the bottom of the email.'); }}>⊘ Report Spam</button>
                </div>
              </div>
              <div className="email-detail-content-wrapper">
                <div className="email-detail-content">
                  <h3 className="email-detail-subject">{selectedEmail.subject}</h3>
                <div className="email-detail-metadata">
                  <div className="sender-avatar">
                    {selectedEmail.sender.charAt(0).toUpperCase()}
                  </div>
                  <div className="sender-info">
                    <div className="sender-line">
                      <span className="email-detail-sender">{selectedEmail.sender.split('<')[0].trim()}</span>
                      <span className="email-detail-address">&lt;{selectedEmail.sender.match(/<(.+)>/)?.[1] || selectedEmail.sender}&gt;</span>
                    </div>
                    <div className="recipient-line">To: You</div>
                    <div className="email-detail-time">{new Date().toLocaleDateString()} {selectedEmail.time}</div>
                  </div>
                </div>
                </div>
                <div className="email-detail-body">
                {selectedEmail.fullContent ? (
                  <div 
                    className="email-body"
                    dangerouslySetInnerHTML={{ __html: selectedEmail.fullContent }}
                    onMouseOver={(e) => {
                      const target = e.target as HTMLElement;
                      if (target.classList.contains('email-link')) {
                        const url = target.getAttribute('data-url');
                        const trackId = `link-${selectedEmail.id}-${url}`;
                        if (!target.getAttribute('data-tracking')) {
                          target.setAttribute('data-tracking', 'true');
                          trackHover(trackId, 'enter', {
                            emailId: selectedEmail.id,
                            isPhishing: selectedEmail.isPhishing,
                            linkUrl: url,
                            linkText: target.textContent
                          });
                        }
                      }
                    }}
                    onMouseOut={(e) => {
                      const target = e.target as HTMLElement;
                      if (target.classList.contains('email-link')) {
                        const url = target.getAttribute('data-url');
                        const trackId = `link-${selectedEmail.id}-${url}`;
                        target.removeAttribute('data-tracking');
                        trackHover(trackId, 'leave', {
                          emailId: selectedEmail.id,
                          isPhishing: selectedEmail.isPhishing,
                          linkUrl: url
                        });
                      }
                    }}
                    onClick={(e) => {
                      const target = e.target as HTMLElement;
                      if (target.classList.contains('email-link')) {
                        e.preventDefault();
                        const url = target.getAttribute('data-url');
                        if (url) {
                          handleLinkClick(selectedEmail, { url, text: target.textContent || '' }, e as any);
                        }
                      }
                    }}
                  />
                ) : (
                  <div className="email-body">
                    <p>{selectedEmail.preview}</p>
                  </div>
                )}
                </div>
              </div>
              
              {/* EmailDecisionBar positioned at bottom */}
              <EmailDecisionBar
                onDecision={(decision) => handleEmailDecision(selectedEmail.id, decision)}
                currentDecision={selectedEmail.decision}
                disabled={!!selectedEmail.decision}
              />
            </div>
          ) : (
            <div className="email-detail">
              <div className="no-email-selected">
                <svg className="empty-icon" width="48" height="48" viewBox="0 0 48 48" fill="currentColor">
                  <path d="M42 15V39C42 40.65 40.65 42 39 42H9C7.35 42 6 40.65 6 39V15L24 27L42 15ZM39 12C40.65 12 42 13.35 42 15L24 27L6 15C6 13.35 7.35 12 9 12H39Z"/>
                </svg>
                <p className="empty-text">Select an item to read</p>
                <p className="empty-subtext">Nothing is selected</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="task-actions">
        <button 
          className="btn btn-secondary"
          onClick={() => tracker.track('helpRequest', { phase: 'email-task' })}
        >
          Security Tips
        </button>
        <button
          className="btn btn-primary"
          onClick={handleComplete}
          disabled={!getAllEmailsProcessed()}
          title={!getAllEmailsProcessed() ? 'Please make decisions on all emails before completing' : ''}
        >
          {getAllEmailsProcessed() ? 'Completed' : 'Complete'} Email Task ({getProgressStats().decidedUnreadEmails}/{getProgressStats().totalUnreadEmails})
        </button>
      </div>


      {showMicroSurvey && microSurveyContext && (
        <MicroSurvey
          emailId={microSurveyContext.emailId}
          taskType="email"
          emailCategory={emails.find(e => e.id === microSurveyContext.emailId)?.emailCategory}
          actionTaken={microSurveyContext.action as any}
          onSubmit={handleMicroSurveyResponse}
          onClose={() => {
            tracker.track('microSurveySkipped', microSurveyContext);
            setShowMicroSurvey(false);
            setMicroSurveyContext(null);
          }}
        />
      )}
    </div>
  );
}

export default EmailTask;
